import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import useBrains from '@/hooks/useBrains';
import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';
import { createYupResolver } from '@/util/forms/createYupResolver';

import { CampaignBuildForm } from '../../components/BuildSteps/CampaignBuildForm';
import { BuildPageLoadingState } from '../../components/CampaignLoadingState';
import { useCampaignBuildFormInit } from '../../hooks/useCampaignBuildFormInit';
import { useCampaigns } from '../../hooks/useCampaigns';
import { invalidateCampaignSenders, useCampaignSenders } from '../../hooks/useCampaignSenders';
import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import { useRegisterWizardStep } from '../../hooks/useWizardStepController';
import {
  CampaignForm,
  DEFAULT_NO_RESPONSE_WAIT_MINUTES,
  EMPTY_SENDER,
  replaceCampaignSendersSequentially,
} from '../../utils';
import { getFormUnresolvedVariables } from '../../utils/utils';
import { campaignBuildSchema } from '../../validator';

/**
 * Step 2a: Standard messaging (autonomous actions OFF)
 * Wraps existing CampaignBuildForm with unified navigation paths
 */
export const MessagingStandard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);

  const campaignId = useSelector(selectCampaignId);
  const slug = useSelector(selectAccountSlug);

  const { getStatus } = useCampaigns(campaignId);
  const {
    campaign_senders,
    createCampaignSenderAsync,
    updateCampaignSenderAsync,
    deleteCampaignSenderAsync,
  } = useCampaignSenders(campaignId);
  const { listStatus: brainsListStatus } = useBrains();
  const { campaign_subscribers } = useCampaignSubscribers(campaignId);

  const methods = useForm<CampaignForm>({
    resolver: createYupResolver<CampaignForm>(campaignBuildSchema),
    defaultValues: {
      primarySender: { ...EMPTY_SENDER },
      fallbackSenders: [
        { ...EMPTY_SENDER },
        { ...EMPTY_SENDER, waitTimeMinutes: DEFAULT_NO_RESPONSE_WAIT_MINUTES },
      ],
      fallbackIfNotDelivered: false,
      fallbackIfNoResponse: false,
    },
  });

  const { listStatus: sendersListStatus, resetInitialization } = useCampaignBuildFormInit(
    campaignId,
    methods,
    setFormKey
  );

  useSegments(PageName.CAMPAIGN_MESSAGING);

  const validateBuildForm = useCallback(async (): Promise<boolean> => {
    const isFormValid = await methods.trigger();
    if (!isFormValid) {
      toast.error(t('campaigns.build.fix_errors_before_continue'));
      return false;
    }

    const unresolved = getFormUnresolvedVariables(methods.getValues(), campaign_subscribers);
    if (unresolved.length > 0) {
      methods.setError('root', {
        type: 'manual',
        message: t('campaigns.missing_variables', { 0: unresolved.join(', ') }),
      });
      toast.error(t('campaigns.missing_variables', { 0: unresolved.join(', ') }));
      return false;
    }

    return true;
  }, [methods, campaign_subscribers, t]);

  const saveSenderData = useCallback(
    async (validateAll: boolean = true): Promise<boolean> => {
      if (validateAll) {
        const isValid = await validateBuildForm();
        if (!isValid) {
          return false;
        }
      }

      try {
        const formData = methods.getValues();
        const hasPrimarySender = formData.primarySender?.integrationId?.trim();
        const hasExistingSenders = campaign_senders && campaign_senders.length > 0;

        if (hasPrimarySender) {
          await replaceCampaignSendersSequentially(
            campaignId,
            formData,
            campaign_senders || [],
            createCampaignSenderAsync,
            updateCampaignSenderAsync,
            (cId: string, senderId: string) =>
              deleteCampaignSenderAsync({ campaign_id: cId, sender_id: senderId })
          );
        } else if (hasExistingSenders) {
          for (const existingSender of campaign_senders) {
            await deleteCampaignSenderAsync({
              campaign_id: campaignId,
              sender_id: existingSender.sender_id,
            });
          }
        }

        resetInitialization();
        invalidateCampaignSenders(queryClient, campaignId);
        return true;
      } catch (error) {
        console.error('Error saving campaign:', error);
        return false;
      }
    },
    [
      campaignId,
      campaign_senders,
      createCampaignSenderAsync,
      deleteCampaignSenderAsync,
      methods,
      queryClient,
      resetInitialization,
      updateCampaignSenderAsync,
      validateBuildForm,
    ]
  );

  const handleSaveAndExit = useCallback(async () => {
    trackEvent(EventName.CampaignMessagingSaveAndExit, {
      campaign_id: campaignId,
      campaign_type: 'marketing',
    });
    const isValid = await saveSenderData(false);
    if (!isValid) {
      return;
    }
    navigate(`/${slug}/campaigns`);
  }, [saveSenderData, navigate, slug, campaignId]);

  // Forward gate for the wizard: track + persist, return whether we may advance.
  const validateMessaging = useCallback(async (): Promise<boolean> => {
    const formData = methods.getValues();
    trackEvent(EventName.CampaignMessagingContinue, {
      campaign_id: campaignId,
      campaign_type: 'marketing',
      channel_type: formData.primarySender?.integrationId || '',
      has_fallback: formData.fallbackIfNotDelivered || formData.fallbackIfNoResponse || false,
    });
    return saveSenderData(true);
  }, [saveSenderData, campaignId, methods]);

  const isLoadingEssentialData =
    getStatus === 'pending' || brainsListStatus === 'pending' || sendersListStatus === 'pending';

  useRegisterWizardStep(
    { validate: validateMessaging, saveAndExit: handleSaveAndExit },
    isLoadingEssentialData
  );

  if (isLoadingEssentialData) {
    return <BuildPageLoadingState />;
  }

  return <CampaignBuildForm key={formKey} methods={methods} />;
};
